/**
 * A small pen for drawing the permit form as PDF (ADR 0289).
 *
 * The PDF library is handed in rather than imported, so the drawing can be
 * tested against pdf-lib in node and the browser can load the library only
 * when somebody saves a permit. The interfaces below are the handful of its
 * calls this uses.
 *
 * Which library a new browser PDF uses: jsPDF, through `../orderPdfKit.ts`,
 * like the receipt, the invoice, the document and both orders. This form is
 * the exception because it is a copy of a paper form with boxes placed to the
 * point, drawn with pdf-lib, the library the crew packet Edge Function already
 * draws its forms with; it came from that work (#849) and was kept rather
 * than redrawn. Do not reach for this kit for a letterhead-and-table page.
 */

export interface PdfColor {
  readonly type?: unknown;
}

export interface PdfFont {
  widthOfTextAtSize(text: string, size: number): number;
}

export interface PdfPage {
  drawText(
    text: string,
    options: {
      x: number;
      y: number;
      size: number;
      font: PdfFont;
      color?: PdfColor;
    },
  ): void;
  drawLine(options: {
    start: { x: number; y: number };
    end: { x: number; y: number };
    thickness?: number;
    color?: PdfColor;
  }): void;
  drawRectangle(options: {
    x: number;
    y: number;
    width: number;
    height: number;
    color?: PdfColor;
    borderColor?: PdfColor;
    borderWidth?: number;
  }): void;
}

export interface PdfDocument {
  addPage(size: [number, number]): PdfPage;
  embedFont(name: string): Promise<PdfFont>;
  setTitle(title: string): void;
  save(): Promise<Uint8Array>;
}

export interface PdfLib {
  PDFDocument: { create(): Promise<PdfDocument> };
  StandardFonts: {
    TimesRoman: string;
    TimesRomanBold: string;
    TimesRomanBoldItalic: string;
    Helvetica: string;
  };
  rgb(red: number, green: number, blue: number): PdfColor;
}

/** Letter paper, in points. */
export const PAGE_WIDTH = 612;
export const PAGE_HEIGHT = 792;

export interface Fonts {
  /** The city's printed words. */
  print: PdfFont;
  bold: PdfFont;
  /** What the office filled in. */
  fill: PdfFont;
  /** A signature. The standard fonts have no script face; this is the nearest. */
  sign: PdfFont;
}

export interface Inks {
  black: PdfColor;
  /** The pen colour the filled boxes are written in, as on screen. */
  pen: PdfColor;
  pink: PdfColor;
}

export async function loadFonts(lib: PdfLib, doc: PdfDocument): Promise<Fonts> {
  const [print, bold, fill, sign] = await Promise.all([
    doc.embedFont(lib.StandardFonts.TimesRoman),
    doc.embedFont(lib.StandardFonts.TimesRomanBold),
    doc.embedFont(lib.StandardFonts.Helvetica),
    doc.embedFont(lib.StandardFonts.TimesRomanBoldItalic),
  ]);
  return { print, bold, fill, sign };
}

export function inks(lib: PdfLib): Inks {
  return {
    black: lib.rgb(0.07, 0.07, 0.07),
    pen: lib.rgb(0.1, 0.24, 0.56),
    pink: lib.rgb(0.969, 0.827, 0.867),
  };
}

/**
 * Only what the standard fonts can draw.
 *
 * They encode WinAnsi and throw on anything else, and a form is typed into by
 * people: an emoji in a name must not stop the permit being saved. Curly
 * quotes, dashes and accented letters survive.
 */
export function printable(text: string): string {
  return text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/…/g, "...")
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, "");
}

/** Words broken into lines no wider than `width` at `size`. */
export function wrap(
  text: string,
  font: PdfFont,
  size: number,
  width: number,
): string[] {
  const lines: string[] = [];
  for (const paragraph of printable(text).split("\n")) {
    let line = "";
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (line && font.widthOfTextAtSize(next, size) > width) {
        lines.push(line);
        line = word;
      } else {
        line = next;
      }
    }
    lines.push(line);
  }
  return lines;
}

/**
 * The pen: where it is on the page, and what it can draw.
 *
 * `y` runs down the page from the top margin, the way the form reads, and is
 * turned into PDF's bottom-up coordinates only when something is drawn.
 */
export class Pen {
  y: number;

  constructor(
    readonly page: PdfPage,
    readonly fonts: Fonts,
    readonly ink: Inks,
    top: number,
  ) {
    this.y = top;
  }

  private at(y: number): number {
    return PAGE_HEIGHT - y;
  }

  text(
    words: string,
    x: number,
    y: number,
    options: { size?: number; font?: PdfFont; pen?: boolean } = {},
  ): number {
    const size = options.size ?? 8;
    const font = options.font ?? this.fonts.print;
    const clean = printable(words);
    if (clean) {
      this.page.drawText(clean, {
        x,
        y: this.at(y),
        size,
        font,
        color: options.pen ? this.ink.pen : this.ink.black,
      });
    }
    return x + font.widthOfTextAtSize(clean, size);
  }

  /** Centred between two x positions. */
  centre(words: string, left: number, right: number, y: number, size = 9) {
    const width = this.fonts.bold.widthOfTextAtSize(printable(words), size);
    this.text(words, left + (right - left - width) / 2, y, {
      size,
      font: this.fonts.bold,
    });
  }

  line(x1: number, y1: number, x2: number, y2: number, thickness = 0.6) {
    this.page.drawLine({
      start: { x: x1, y: this.at(y1) },
      end: { x: x2, y: this.at(y2) },
      thickness,
      color: this.ink.black,
    });
  }

  /** A frame, from its top-left corner. */
  frame(x: number, top: number, width: number, height: number) {
    this.page.drawRectangle({
      x,
      y: this.at(top + height),
      width,
      height,
      borderColor: this.ink.black,
      borderWidth: 0.8,
    });
  }

  fill(x: number, top: number, width: number, height: number, color: PdfColor) {
    this.page.drawRectangle({
      x,
      y: this.at(top + height),
      width,
      height,
      color,
    });
  }

  /** A tick box, its label after it. Returns where the label ends. */
  box(
    x: number,
    y: number,
    label: string,
    checked = false,
    size = 7.5,
  ): number {
    const side = size * 0.95;
    this.page.drawRectangle({
      x,
      y: this.at(y),
      width: side,
      height: side,
      borderColor: this.ink.black,
      borderWidth: 0.6,
    });
    if (checked) {
      this.page.drawLine({
        start: { x: x + 1.2, y: this.at(y) + side * 0.5 },
        end: { x: x + side * 0.4, y: this.at(y) + 1.2 },
        thickness: 1.1,
        color: this.ink.pen,
      });
      this.page.drawLine({
        start: { x: x + side * 0.4, y: this.at(y) + 1.2 },
        end: { x: x + side + 1, y: this.at(y) + side + 1 },
        thickness: 1.1,
        color: this.ink.pen,
      });
    }
    return this.text(label, x + side + 2.5, y, { size }) + 6;
  }

  /**
   * A label and the line it is written on, with the value in pen above it.
   * The line runs from the label's end to `right`.
   */
  field(
    label: string,
    value: string,
    x: number,
    right: number,
    y: number,
    size = 8,
  ) {
    const start = label ? this.text(label, x, y, { size }) + 3 : x;
    this.line(start, y + 1.5, right, y + 1.5);
    if (value) {
      this.text(value, start + 2, y - 0.5, {
        size: size + 0.5,
        font: this.fonts.fill,
        pen: true,
      });
    }
  }

  /** A signature on its line: the name in the signing face, in pen. */
  signature(
    label: string,
    name: string,
    x: number,
    right: number,
    y: number,
    size = 8,
  ) {
    const start = this.text(label, x, y, { size }) + 3;
    this.line(start, y + 1.5, right, y + 1.5);
    if (name) {
      this.text(name, start + 4, y - 1, {
        size: 14,
        font: this.fonts.sign,
        pen: true,
      });
    }
  }

  /** A paragraph, wrapped, moving the pen down past it. */
  paragraph(
    words: string,
    x: number,
    width: number,
    options: { size?: number; bold?: boolean; leading?: number } = {},
  ) {
    const size = options.size ?? 8;
    const font = options.bold ? this.fonts.bold : this.fonts.print;
    const leading = options.leading ?? size * 1.22;
    for (const row of wrap(words, font, size, width)) {
      this.y += leading;
      this.text(row, x, this.y, { size, font });
    }
  }
}
